import type { Brand } from '@/types/auth.types';

/**
 * BigQuery `account_group_code` used in BFF query filters.
 * Catalog `GET .../account-groups` maps `id` to that code; `account_group_code` may duplicate it when present.
 */
export function getBrandAccountGroupCode(brand: Brand): string | null {
  const explicit = brand.account_group_code?.trim();
  const fromId = brand.id?.trim();
  const code = explicit || fromId;
  return code?.length ? code : null;
}

export function isBrandSelectableForFilters(brand: Brand): boolean {
  return getBrandAccountGroupCode(brand) !== null;
}

/**
 * Resolve UI selection (catalog row `id`, same as BQ code per BFF) to deduped non-empty codes for API query params.
 */
function findBrandBySelectionKey(brands: Brand[], key: string): Brand | undefined {
  return brands.find(b => b.id === key || (b.account_group_code?.trim() ?? '') === key);
}

function tryPushAccountGroupCode(
  brands: Brand[],
  seen: Set<string>,
  codes: string[],
  key: string
): void {
  const brand = findBrandBySelectionKey(brands, key);
  if (!brand) return;

  const code = getBrandAccountGroupCode(brand);
  if (!code || seen.has(code)) return;
  seen.add(code);
  codes.push(code);
}

export function mapBrandSelectionToAccountGroupCodes(
  brands: Brand[],
  selectedIds: string[]
): string[] {
  const codes: string[] = [];
  const seen = new Set<string>();

  for (const raw of selectedIds) {
    const key = raw?.trim();
    if (!key) continue;
    tryPushAccountGroupCode(brands, seen, codes, key);
  }

  return codes;
}

/**
 * Inverse of `mapBrandSelectionToAccountGroupCodes`: BQ codes back to catalog row ids.
 *
 * The market endpoint hands back the account-group codes that belong to a market, but
 * the brand picker is keyed on catalog row ids, so one of the two has to be translated.
 * It is done here rather than by widening `Brand`: `state.auth.brands` carries no
 * country and no vertical, and it must stay that way — the moment the FE knew which
 * brand belongs to which market on its own, that knowledge would be a second source of
 * truth to keep in sync with the warehouse.
 *
 * Matches on either key for the same reason `findBrandBySelectionKey` does: the catalog
 * exposes `id` and `account_group_code`, and they are not always the same string.
 * Unknown codes are skipped, and the result follows the order of `codes`.
 */
export function mapAccountGroupCodesToBrandIds(brands: Brand[], codes: string[]): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();

  for (const raw of codes) {
    const key = raw?.trim();
    if (!key) continue;

    const brand = findBrandBySelectionKey(brands, key);
    if (!brand || seen.has(brand.id)) continue;

    seen.add(brand.id);
    ids.push(brand.id);
  }

  return ids;
}
